import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type Language = 'zh' | 'en' | 'ja';

type TranslationValues = Record<string, string | number>;

type I18nContextValue = {
  language: Language;
  locale: string;
  setLanguage: (language: Language) => void;
  t: (key: string, values?: TranslationValues) => string;
};

const storageKey = 'oapm-language';

const languageMeta: Record<Language, { short: string; label: string; locale: string }> = {
  zh: { short: '中', label: '中文', locale: 'zh-CN' },
  en: { short: 'EN', label: 'English', locale: 'en-US' },
  ja: { short: '日', label: '日本語', locale: 'ja-JP' },
};

const messages: Record<Language, Record<string, string>> = {
  zh: {
    'app.name': 'OA Plaza 监控',
    'app.fullName': 'OAPlazaMonitor',
    'app.company': '株式会社オーエープラザ',
    'app.returnOverview': '返回总览',
    'nav.overview': '总览',
    'nav.live': '实时监控',
    'nav.playback': '录像回放',
    'nav.streams': '视频流管理',
    'nav.system': '系统状态',
    'nav.main': '主导航',
    'language.label': '语言',
    'language.zh': '中文',
    'language.en': 'English',
    'language.ja': '日本語',

    'splash.bootAria': 'OAPlazaMonitor 正在启动',
    'splash.loginAria': 'OAPlazaMonitor 正在进入工作台',
    'splash.subtitle': '实时监控与录像回放工作台',
    'splash.booting': '正在载入监控工作区',
    'splash.loginSuccess': '登录成功，正在进入工作台',
    'splash.ready': '准备就绪',

    'login.title': '公司监控控制台',
    'login.username': '用户名',
    'login.password': '密码',
    'login.submit': '登录',
    'login.submitting': '正在登录',
    'login.failed': '登录失败。',

    'account.admin': '管理员',
    'account.user': '普通用户',
    'account.editCurrent': '编辑当前用户',
    'account.changePassword': '修改密码',
    'account.manageUsers': '用户管理',
    'account.logout': '退出登录',
    'account.currentUser': '当前用户',
    'account.username': '用户名',
    'account.role': '角色',
    'account.displayName': '显示名称',
    'account.currentPassword': '当前密码',
    'account.newPassword': '新密码',
    'account.confirmPassword': '确认新密码',
    'account.passwordMismatch': '两次输入的新密码不一致。',
    'account.profileUpdated': '显示名称已更新。',
    'account.passwordUpdated': '密码已修改。',
    'account.passwordHint': '密码不能为空。修改后当前登录会话保持有效。',
    'common.close': '关闭',
    'common.cancel': '取消',
    'common.save': '保存',
    'common.edit': '编辑',
    'common.delete': '删除',
    'common.refresh': '刷新',
    'common.refreshing': '刷新中',
    'common.processing': '处理中',
    'common.loading': '正在加载…',
    'common.enabled': '已启用',
    'common.disabled': '已停用',
    'common.online': '在线',
    'common.offline': '离线',
    'common.unknown': '未知',
    'common.previous': '上一个',
    'common.next': '下一个',
    'common.fullscreen': '全屏',
    'common.exitFullscreen': '退出全屏',
    'common.play': '播放',
    'common.pause': '暂停',
    'common.stop': '停止播放',
    'common.back10': '后退 10 秒',
    'common.forward10': '前进 10 秒',
    'common.retry': '重新连接',
    'common.neverLogin': '从未登录',
    'common.operationFailed': '操作失败。',

    'overview.eyebrow': 'BUSINESS OVERVIEW',
    'overview.title': '总览',
    'overview.reading': '正在读取',
    'overview.updatedAt': '更新于 {time}',
    'overview.brandTitle': '监控与录像工作台',
    'overview.live': '实时监控',
    'overview.playback': '录像回放',
    'overview.streams': '视频流管理',
    'overview.videoSources': '视频源',
    'overview.onlineCount': '{count} 路在线',
    'overview.offlineSources': '离线视频源',
    'overview.offlineDetail': '已启用但当前离线',
    'overview.disabledSources': '已停用',
    'overview.disabledDetail': '不向普通播放列表展示',
    'overview.activeUpstreams': '活跃上游流',
    'overview.upstreamDetail': 'AKStream / ZLMediaKit',
    'overview.liveSessions': '实时观看会话',
    'overview.activeUsers': '{count} 名活跃用户',
    'overview.playbackSessions': '录像回放会话',
    'overview.playbackDetail': '当前历史流',
    'overview.recentEvents': 'RECENT EVENTS',
    'overview.recentErrors': '最近异常摘要',
    'overview.media': '媒体',
    'overview.system': '系统',
    'overview.loadFailed': '读取业务摘要失败。',

    'live.batchTitle': '批量播放',
    'live.importedCount': '{count} 路已导入',
    'live.singleTitle': '单独播放',
    'live.singleCount': '{count} 路可选摄像头',
    'live.search': '搜索视频源',
    'live.allStatus': '全部状态',
    'live.noSources': '没有符合条件的视频源。',
    'live.loadingCatalog': '正在读取目录…',
    'live.catalogFailed': '读取视频源目录失败。',
    'live.dragTitle': '单击加入首个空格子，或拖动到指定画面',
    'live.emptyTitle': '这里还没有画面',
    'live.emptyHint': '请从左侧选择或拖拽摄像头到此播放',
    'live.loadingStream': '正在申请 NVR 视频流',
    'live.reconnect': '重连',
    'live.close': '关闭',
    'live.layoutLabel': '{columns}×{columns} 监控布局',
    'live.workspaceNotReady': '实时监控工作区尚未就绪，请稍后再试。',
    'live.workspaceTimeout': '取得实时监控工作区超时，请刷新页面重试。',
    'live.workspaceFailed': '无法取得实时监控工作区，请刷新页面重试。',
    'live.workspaceClaimed': '已接管当前用户的实时监控工作区，并清理旧页面遗留的观看会话。',
    'live.workspaceTaken': '当前页面的实时监控工作区已被另一个页面接管，请刷新后重新连接。',
    'live.workspaceTakenTile': '工作区已被其他页面接管，请刷新页面。',
    'live.sessionExpired': '观看会话已过期，请重新连接。',
    'live.unknownSessionsCleaned': '已清理 {count} 个浏览器未知的遗留观看会话。',
    'live.loginExpired': '登录会话已失效，请重新登录。',
    'live.alreadyInTile': '“{name}”已经在画面 {index} 中。',
    'live.alreadyAssigned': '“{name}”已经分配到画面 {index}。如需恢复，请使用该画面的“重连”。',
    'live.noEmptyTile': '当前布局没有空格子。可以将视频源拖到已有画面上进行替换。',
    'live.nvrAlreadyRunning': '“{name}”的 {count} 路实时画面正在后台持续运行，无需重新上屏。',
    'live.nvrStarting': '正在将“{name}”的 {count} 路视频依次上屏。',
    'live.nvrSubmitted': '“{name}”已提交 {count} 路实时画面，正在等待全部出画。',
    'live.startTimeout': 'NVR 实时视频启动超时，观看会话将由工作区自动回收。',
    'live.startFailed': '启动实时流失败。',
    'live.mediaTimeout': '媒体流在 {seconds} 秒内未出画，观看会话已释放，请重新连接。',
    'live.mediaPlayerError': '浏览器在多次重试后仍无法播放该视频流，观看会话已释放。',
    'live.releaseTimeout': '释放实时观看会话超时，本地格子已经清空。',
    'live.sourceGroupHint': '将 {count} 路视频直接铺满 4×4 监控矩阵',
    'live.previousCamera': '上一个摄像头',
    'live.nextCamera': '下一个摄像头',
    'live.mediaFailureReportTimeout': '媒体失败回报超时，本地会话已经释放。',
    'live.workspaceHeartbeatTimeout': '实时监控工作区心跳超时。',
    'live.positionSyncTimeout': '画面位置同步超时。',
    'live.positionSyncFailed': '画面已交换，但会话位置记录稍后同步失败。',
    'live.positionSyncFailedDetail': '画面已交换，但会话位置记录稍后同步失败：{detail}',
    'live.tileName': '画面 {index}',

    'playback.eyebrow': 'NVR PLAYBACK',
    'playback.title': '录像回放',
    'playback.selectSource': '请选择视频源',
    'playback.waitQuery': '等待查询',
    'playback.noSources': '没有支持录像查询的视频源',
    'playback.searchSource': '搜索摄像头名称、区域或 NVR',
    'playback.chooseSource': '选择支持录像的摄像头',
    'playback.query': '查询录像',
    'playback.querying': '查询中…',
    'playback.statusQuerying': '正在查询录像',
    'playback.statusOpening': '正在启动历史流',
    'playback.statusSeeking': '正在重建目标录像流',
    'playback.stop': '停止播放',
    'playback.unclassified': '未分类',
    'playback.unknownNvr': '未知 NVR',
    'playback.nvrClock': 'NVR 时钟：{date} · 时区基准：{zone} · 目录：{received}/{total}',
    'playback.segments': '录像片段',
    'playback.noSegments': '尚未取得录像片段。',
    'playback.audioUnsupported': '音频不受支持',
    'playback.enableAudio': '启用声音',
    'playback.mute': '静音',
    'playback.volume': '音量',
    'playback.rate': '倍速',
    'playback.timelineLabel': '24 小时录像自由进度条',
    'playback.dateLabel': '选择录像查询日期',
    'playback.chooseSourceError': '请先选择视频源。',
    'playback.queryFailed': '录像查询失败。',
    'playback.playFailed': '历史流启动失败。',
    'playback.noRecordingAt': '所选时间 {time} 没有录像。',
    'playback.noRecordingStatus': '该时间点没有录像',
    'playback.firstSegment': '上一录像片段',
    'playback.nextSegment': '下一录像片段',
    'playback.pausedAt': '历史录像已暂停 · {time}',
    'playback.playingAt': '历史录像播放中 · {time}',
    'playback.stopped': '录像播放已停止',
    'playback.queryDateChanged': '日期已更改，请重新查询',
    'playback.foundSegments': '找到 {count} 个录像片段{suffix}',
    'playback.incompleteSuffix': '（目录未完整）',
    'playback.noRecordingDay': '当天没有录像片段',
    'playback.queryTimeout': '录像目录查询没有返回结果，请稍后再试。',
    'playback.queryPartial': '录像目录在 {seconds} 秒内未完整返回，当前先显示已收到的 {count} 个片段。',
    'playback.reinviteNotice': '已通过新的 GB28181 INVITE 从目标时间重新开播，不再使用 INFO 定位。',
    'playback.reinviteDirect': '已通过新的 GB28181 INVITE 从目标时间直接开播。',
    'playback.audioUnavailableNotice': '该录像音频编码不受浏览器支持，已自动恢复为静音视频播放。',
    'playback.audioUnavailableStatus': '音频不可用，视频继续播放',
    'playback.selectFirst': '请先开始播放录像。',
    'playback.sourcesLoadFailed': '读取录像视频源失败。',
    'playback.heartbeatTimeout': '历史回放心跳超时。',
    'playback.rebuildAt': '正在从 {time} 重新建立历史流',
    'playback.rebuildTimeout': '目标时间历史流重建超时，原录像会话仍会保留。',
    'playback.targetReady': '目标时间历史流已建立，正在等待媒体画面',
    'playback.rebuildFailed': '目标时间历史流重建失败。',
    'playback.originalContinues': '重建失败，原录像仍可继续播放',
    'playback.stopUnconfirmedLocal': '历史流停止请求未确认，本地会话已经释放。',
    'playback.stopBackgroundCleanup': '历史流停止请求未确认，后台会继续异步回收。',
    'playback.creatingQuery': '正在创建录像查询任务',
    'playback.queryTask': '正在查询 taskId {taskId}',
    'playback.readResultTimeout': '读取录像目录结果超时。',
    'playback.receivingCatalog': '正在接收录像目录 {received}/{total}',
    'playback.queryFailedStatus': '查询失败',
    'playback.openingAt': '正在从 {time} 启动历史流',
    'playback.openTimeout': '历史流启动超时，请稍后再试。',
    'playback.releaseExpiredTimeout': '释放过期历史会话超时。',
    'playback.startedWaiting': '历史流已启动，正在等待媒体画面',
    'playback.playFailedStatus': '播放失败',
    'playback.audioUnsupportedRetry': '该录像音频编码不受浏览器支持，当前保持静音播放。切换片段后可重新尝试。',
    'playback.tryingAudio': '正在尝试接入录像音轨',

    'player.idle': '请选择要观看的摄像头',
    'player.connecting': '正在连接媒体流',
    'player.unsupported': '当前浏览器不支持 MSE/FLV 播放。',
    'player.playFailed': '播放器启动失败。',
    'player.resumeFailed': '继续播放失败。',
    'player.paused': '已暂停',
    'player.diagnostics': '查看诊断信息',
    'player.codecUnsupported': '媒体编码或封装参数不受当前浏览器解码链支持。请展开诊断信息。',
    'player.mediaError': '媒体错误：{detail}',
    'player.backgroundPaused': '浏览器已暂停后台视频，返回当前页面后会自动恢复',
    'player.retrying': '媒体端点正在重试（第 {attempt} 次）',
    'player.retryLater': '媒体流暂未就绪，{delay} ms 后自动重试',
    'player.audioFallback': '该录像音频编码不受浏览器支持，正在恢复静音画面',

    'calendar.selectDate': '选择日期',
    'calendar.previousMonth': '上一个月',
    'calendar.nextMonth': '下一个月',
    'calendar.monthTitle': '{year} 年 {month} 月',
    'calendar.today': '今天',

    'users.eyebrow': 'ACCESS CONTROL',
    'users.title': '用户管理',
    'users.subtitle': '创建、编辑、启停与重置密码',
    'users.username': '用户名',
    'users.displayName': '显示名称',
    'users.initialPassword': '初始密码',
    'users.create': '创建用户',
    'users.loading': '正在读取用户…',
    'users.user': '用户',
    'users.role': '角色',
    'users.status': '状态',
    'users.lastLogin': '最后登录',
    'users.createdAt': '创建时间',
    'users.actions': '操作',
    'users.resetPassword': '重置密码',
    'users.enableUser': '启用此用户',
    'users.currentRestriction': '当前用户不能在此处修改自己的角色或启用状态。',
    'users.passwordNotEmpty': '密码不能为空。重置操作不会显示或记录密码内容。',

    'streams.eyebrow': 'VIDEO CATALOG',
    'streams.title': '视频流管理',
    'streams.subtitle': '统一管理 NVR、GB28181 通道与 OAPM 业务设置。',
    'streams.customAreas': '＋ 自定义区域',
    'streams.refreshStatus': '刷新状态',
    'streams.syncNow': '立即同步',
    'streams.sources': '视频源',
    'streams.online': '在线',
    'streams.disabled': '已停用',
    'streams.discovered': '新发现',
    'streams.nvrs': 'NVR 设备',
    'streams.syncLogs': '同步记录',
    'streams.editNvr': '编辑 NVR 名称',
    'streams.nvrDisplayName': 'NVR 显示名称',
    'streams.nvrNameSaved': 'NVR 名称已保存。',
    'streams.channels': '发现通道',
    'streams.imported': '已导入',
    'streams.missing': '失联',
    'streams.lastSync': '最后同步',
    'streams.editSource': '编辑视频源',
    'streams.area': '所属区域',
    'streams.manageAreas': '新建 / 管理',
    'streams.sortOrder': '排序',
    'streams.enableOapm': '在 OAPM 中启用',
    'streams.notes': '备注',
    'streams.currentFilter': '当前筛选范围',
    'streams.pageLimit': '本页 50 路以内',
    'streams.pageStats': '本页统计',
    'streams.waitImport': '等待导入',
    'streams.onlineNvrs': '{count} 台在线',
    'streams.searchPlaceholder': '搜索名称、DeviceId、ChannelId、MainId',
    'streams.allAreas': '全部区域',
    'streams.allOnline': '全部在线状态',
    'streams.allEnabled': '全部启用状态',
    'streams.allDiscovery': '全部发现状态',
    'streams.ignored': '已忽略',
    'streams.source': '视频源',
    'streams.areaNvr': '区域 / NVR',
    'streams.origin': '来源',
    'streams.catalogStatus': '目录状态',
    'streams.actions': '操作',
    'streams.reading': '正在读取目录…',
    'streams.noResults': '当前筛选条件下没有视频源。',
    'streams.unassigned': '未分区',
    'streams.manualSource': '手工视频源',
    'streams.activated': '已激活',
    'streams.notActivated': '未激活',
    'streams.oapmEnabled': 'OAPM 已启用',
    'streams.oapmDisabled': 'OAPM 已停用',
    'streams.import': '导入',
    'streams.ignore': '忽略',
    'streams.pageSummary': '共 {count} 路，第 {page} / {pages} 页',
    'streams.previousPage': '上一页',
    'streams.nextPage': '下一页',
    'streams.time': '时间',
    'streams.trigger': '触发',
    'streams.status': '状态',
    'streams.added': '新增',
    'streams.updated': '更新',
    'streams.error': '错误',
    'streams.noArea': '不指定区域',
    'streams.areaDirectory': '自定义区域',
    'streams.areaHelp': '新建仓库、办公室、出入口等分组，并分配给视频源。',
    'streams.newArea': '新建区域',
    'streams.name': '名称',
    'streams.enableArea': '启用区域',
    'streams.saveArea': '保存区域',
    'streams.areaCount': '{count} 路 · 排序 {order} · {status}',
    'streams.confirmDeleteArea': '确定删除区域“{name}”吗？区域内必须没有视频源。',
    'streams.confirmIgnore': '确定忽略视频源“{name}”吗？它将从普通播放目录中隐藏。',
    'streams.syncSummary': '同步完成：新增 {added}，更新 {updated}，失联 {missing}。',
    'streams.syncSuccess': '同步完成。',
    'streams.sourceSaved': '视频源设置已保存。',
    'streams.sourceImported': '视频源已导入。',
    'streams.sourceIgnored': '视频源已忽略。',
    'streams.areaUpdated': '区域“{name}”已更新。',
    'streams.areaCreated': '区域“{name}”已创建。',
    'streams.areaCreatedSelected': '区域“{name}”已创建并选中。',
    'streams.areaDeleted': '区域已删除。',

    'system.eyebrow': 'OPERATIONS',
    'system.title': '系统状态',
    'system.refresh': '立即刷新',
    'system.services': '服务状态',
    'system.checking': '正在检查',
    'system.checkedAt': '检查于 {time}',
    'system.resources': '服务器资源',
    'system.resourceHint': '当前采样值，不保存长期历史',
    'system.cpu': 'CPU',
    'system.memory': '内存',
    'system.disk': '磁盘 {name}',
    'system.uptime': '系统运行时间',
    'system.apiProcess': 'OAPM API 进程',
    'system.mediaProcess': 'MediaServer 进程',
    'system.network': '网络状态',
    'system.upload': '当前上传',
    'system.download': '当前下载',
    'system.totalSent': '累计发送',
    'system.totalReceived': '累计接收',
    'system.videoBusiness': '视频业务状态',
    'system.currentSessions': 'OAPM 当前内存会话',
    'system.upstreams': '上游流',
    'system.sessions': '会话',
    'system.users': '用户',
    'system.recentErrors': '最近异常摘要',
    'system.loadFailed': '读取系统状态失败。',
  },
  en: {
    'app.name': 'OA Plaza Monitor',
    'app.fullName': 'OAPlazaMonitor',
    'app.company': 'OA Plaza Co., Ltd.',
    'app.returnOverview': 'Return to overview',
    'nav.overview': 'Overview',
    'nav.live': 'Live',
    'nav.playback': 'Playback',
    'nav.streams': 'Video Sources',
    'nav.system': 'System',
    'nav.main': 'Main navigation',
    'language.label': 'Language',
    'language.zh': '中文',
    'language.en': 'English',
    'language.ja': '日本語',

    'splash.bootAria': 'OAPlazaMonitor is starting',
    'splash.loginAria': 'OAPlazaMonitor is opening the workspace',
    'splash.subtitle': 'Live monitoring and NVR playback workspace',
    'splash.booting': 'Loading monitoring workspace',
    'splash.loginSuccess': 'Signed in. Opening the workspace',
    'splash.ready': 'Ready',

    'login.title': 'Company Monitoring Console',
    'login.username': 'Username',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.submitting': 'Signing in',
    'login.failed': 'Sign-in failed.',

    'account.admin': 'Administrator',
    'account.user': 'User',
    'account.editCurrent': 'Edit profile',
    'account.changePassword': 'Change password',
    'account.manageUsers': 'User management',
    'account.logout': 'Sign out',
    'account.currentUser': 'Current user',
    'account.username': 'Username',
    'account.role': 'Role',
    'account.displayName': 'Display name',
    'account.currentPassword': 'Current password',
    'account.newPassword': 'New password',
    'account.confirmPassword': 'Confirm new password',
    'account.passwordMismatch': 'The new passwords do not match.',
    'account.profileUpdated': 'Display name updated.',
    'account.passwordUpdated': 'Password changed.',
    'account.passwordHint': 'The password cannot be empty. Your current session remains active.',
    'common.close': 'Close',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.refresh': 'Refresh',
    'common.refreshing': 'Refreshing',
    'common.processing': 'Working',
    'common.loading': 'Loading…',
    'common.enabled': 'Enabled',
    'common.disabled': 'Disabled',
    'common.online': 'Online',
    'common.offline': 'Offline',
    'common.unknown': 'Unknown',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.fullscreen': 'Fullscreen',
    'common.exitFullscreen': 'Exit fullscreen',
    'common.play': 'Play',
    'common.pause': 'Pause',
    'common.stop': 'Stop',
    'common.back10': 'Back 10 seconds',
    'common.forward10': 'Forward 10 seconds',
    'common.retry': 'Reconnect',
    'common.neverLogin': 'Never signed in',
    'common.operationFailed': 'Operation failed.',

    'overview.eyebrow': 'BUSINESS OVERVIEW',
    'overview.title': 'Overview',
    'overview.reading': 'Loading',
    'overview.updatedAt': 'Updated at {time}',
    'overview.brandTitle': 'Monitoring and Playback Workspace',
    'overview.live': 'Live Monitoring',
    'overview.playback': 'NVR Playback',
    'overview.streams': 'Video Source Management',
    'overview.videoSources': 'Video Sources',
    'overview.onlineCount': '{count} online',
    'overview.offlineSources': 'Offline Sources',
    'overview.offlineDetail': 'Enabled but currently offline',
    'overview.disabledSources': 'Disabled',
    'overview.disabledDetail': 'Hidden from the standard playback list',
    'overview.activeUpstreams': 'Active Upstreams',
    'overview.upstreamDetail': 'AKStream / ZLMediaKit',
    'overview.liveSessions': 'Live Viewer Sessions',
    'overview.activeUsers': '{count} active users',
    'overview.playbackSessions': 'Playback Sessions',
    'overview.playbackDetail': 'Current historical streams',
    'overview.recentEvents': 'RECENT EVENTS',
    'overview.recentErrors': 'Recent Error Summary',
    'overview.media': 'Media',
    'overview.system': 'System',
    'overview.loadFailed': 'Failed to load business summary.',

    'live.batchTitle': 'Batch Playback',
    'live.importedCount': '{count} sources imported',
    'live.singleTitle': 'Single Playback',
    'live.singleCount': '{count} cameras available',
    'live.search': 'Search video sources',
    'live.allStatus': 'All statuses',
    'live.noSources': 'No matching video sources.',
    'live.loadingCatalog': 'Loading catalog…',
    'live.catalogFailed': 'Failed to load video source catalog.',
    'live.dragTitle': 'Click to add to the first empty tile, or drag to a specific tile',
    'live.emptyTitle': 'No video is loaded here',
    'live.emptyHint': 'Choose or drag a camera from the left to play it here',
    'live.loadingStream': 'Requesting NVR live stream',
    'live.reconnect': 'Reconnect',
    'live.close': 'Close',
    'live.layoutLabel': '{columns}×{columns} monitoring layout',
    'live.workspaceNotReady': 'The live workspace is not ready. Please try again shortly.',
    'live.workspaceTimeout': 'Timed out while acquiring the live workspace. Refresh and try again.',
    'live.workspaceFailed': 'Could not acquire the live workspace. Refresh and try again.',
    'live.workspaceClaimed': 'This page now owns the live workspace and cleaned up stale sessions.',
    'live.workspaceTaken': 'Another page has taken over this live workspace. Refresh to reconnect.',
    'live.workspaceTakenTile': 'The workspace was taken over by another page. Refresh the page.',
    'live.sessionExpired': 'Viewer session expired. Reconnect.',
    'live.unknownSessionsCleaned': 'Cleaned up {count} viewer sessions unknown to this browser.',
    'live.loginExpired': 'Your sign-in session expired. Please sign in again.',
    'live.alreadyInTile': '“{name}” is already in tile {index}.',
    'live.alreadyAssigned': '“{name}” is already assigned to tile {index}. Use Reconnect to recover it.',
    'live.noEmptyTile': 'There are no empty tiles in this layout. Drag a source onto an existing tile to replace it.',
    'live.nvrAlreadyRunning': 'The {count} live sources from “{name}” are still running in the background.',
    'live.nvrStarting': 'Adding {count} sources from “{name}” to the matrix.',
    'live.nvrSubmitted': 'Submitted {count} live sources from “{name}”; waiting for video.',
    'live.startTimeout': 'NVR live start timed out. The workspace will reclaim the session.',
    'live.startFailed': 'Failed to start live stream.',
    'live.mediaTimeout': 'No video appeared within {seconds} seconds. The viewer session was released.',
    'live.mediaPlayerError': 'The browser could not play this stream after retries. The viewer session was released.',
    'live.releaseTimeout': 'Timed out releasing the viewer session. The local tile was cleared.',
    'live.sourceGroupHint': 'Fill the 4×4 matrix with {count} sources',
    'live.previousCamera': 'Previous camera',
    'live.nextCamera': 'Next camera',
    'live.mediaFailureReportTimeout': 'Media failure reporting timed out. The local session has been released.',
    'live.workspaceHeartbeatTimeout': 'Live workspace heartbeat timed out.',
    'live.positionSyncTimeout': 'Tile position synchronization timed out.',
    'live.positionSyncFailed': 'Tiles were swapped, but session positions could not be synchronized.',
    'live.positionSyncFailedDetail': 'Tiles were swapped, but session positions could not be synchronized: {detail}',
    'live.tileName': 'Tile {index}',

    'playback.eyebrow': 'NVR PLAYBACK',
    'playback.title': 'Playback',
    'playback.selectSource': 'Choose a video source',
    'playback.waitQuery': 'Ready to query',
    'playback.noSources': 'No video sources support recording queries',
    'playback.searchSource': 'Search camera, area or NVR',
    'playback.chooseSource': 'Choose a camera with recordings',
    'playback.query': 'Query Recordings',
    'playback.querying': 'Querying…',
    'playback.statusQuerying': 'Querying recordings',
    'playback.statusOpening': 'Starting historical stream',
    'playback.statusSeeking': 'Rebuilding stream at target time',
    'playback.stop': 'Stop Playback',
    'playback.unclassified': 'Unclassified',
    'playback.unknownNvr': 'Unknown NVR',
    'playback.nvrClock': 'NVR clock: {date} · Time zone: {zone} · Catalog: {received}/{total}',
    'playback.segments': 'Recording Segments',
    'playback.noSegments': 'No recording segments loaded.',
    'playback.audioUnsupported': 'Audio unsupported',
    'playback.enableAudio': 'Enable audio',
    'playback.mute': 'Mute',
    'playback.volume': 'Volume',
    'playback.rate': 'Speed',
    'playback.timelineLabel': '24-hour recording timeline',
    'playback.dateLabel': 'Choose recording date',
    'playback.chooseSourceError': 'Choose a video source first.',
    'playback.queryFailed': 'Recording query failed.',
    'playback.playFailed': 'Historical stream failed to start.',
    'playback.noRecordingAt': 'There is no recording at {time}.',
    'playback.noRecordingStatus': 'No recording at this time',
    'playback.firstSegment': 'Previous recording segment',
    'playback.nextSegment': 'Next recording segment',
    'playback.pausedAt': 'Playback paused · {time}',
    'playback.playingAt': 'Playing recording · {time}',
    'playback.stopped': 'Playback stopped',
    'playback.queryDateChanged': 'Date changed. Query again.',
    'playback.foundSegments': 'Found {count} recording segments{suffix}',
    'playback.incompleteSuffix': ' (catalog incomplete)',
    'playback.noRecordingDay': 'No recordings for this date',
    'playback.queryTimeout': 'The recording catalog did not return a result. Try again later.',
    'playback.queryPartial': 'The catalog was incomplete after {seconds} seconds; showing {count} received segments.',
    'playback.reinviteNotice': 'Reopened at the target time with a new GB28181 INVITE; INFO seeking is no longer used.',
    'playback.reinviteDirect': 'Started directly at the target time with a new GB28181 INVITE.',
    'playback.audioUnavailableNotice': 'This recording audio codec is unsupported. Video-only playback was restored.',
    'playback.audioUnavailableStatus': 'Audio unavailable; video continues',
    'playback.selectFirst': 'Start a recording first.',
    'playback.sourcesLoadFailed': 'Failed to load recording-capable sources.',
    'playback.heartbeatTimeout': 'Playback heartbeat timed out.',
    'playback.rebuildAt': 'Rebuilding the history stream from {time}',
    'playback.rebuildTimeout': 'Target-time stream rebuild timed out. The current session is retained.',
    'playback.targetReady': 'Target-time stream created. Waiting for video.',
    'playback.rebuildFailed': 'Target-time stream rebuild failed.',
    'playback.originalContinues': 'Rebuild failed. The current recording can continue.',
    'playback.stopUnconfirmedLocal': 'Playback stop was not confirmed. The local session has been released.',
    'playback.stopBackgroundCleanup': 'Playback stop was not confirmed. Background cleanup will continue.',
    'playback.creatingQuery': 'Creating recording catalog query',
    'playback.queryTask': 'Querying taskId {taskId}',
    'playback.readResultTimeout': 'Reading recording catalog results timed out.',
    'playback.receivingCatalog': 'Receiving recording catalog {received}/{total}',
    'playback.queryFailedStatus': 'Query failed',
    'playback.openingAt': 'Starting history stream from {time}',
    'playback.openTimeout': 'History stream startup timed out. Try again later.',
    'playback.releaseExpiredTimeout': 'Releasing the obsolete playback session timed out.',
    'playback.startedWaiting': 'History stream started. Waiting for video.',
    'playback.playFailedStatus': 'Playback failed',
    'playback.audioUnsupportedRetry': 'This audio codec is unsupported. Video remains muted; try again after changing segments.',
    'playback.tryingAudio': 'Connecting the recording audio track',

    'player.idle': 'Choose a camera to watch',
    'player.connecting': 'Connecting media stream',
    'player.unsupported': 'This browser does not support MSE/FLV playback.',
    'player.playFailed': 'Player failed to start.',
    'player.resumeFailed': 'Could not resume playback.',
    'player.paused': 'Paused',
    'player.diagnostics': 'Show diagnostics',
    'player.codecUnsupported': 'The media codec or container is not supported by this browser. Open diagnostics.',
    'player.mediaError': 'Media error: {detail}',
    'player.backgroundPaused': 'The browser paused background video. It will resume when you return.',
    'player.retrying': 'Retrying media endpoint (attempt {attempt})',
    'player.retryLater': 'Media is not ready. Retrying in {delay} ms',
    'player.audioFallback': 'The recording audio codec is unsupported. Restoring video-only playback',

    'calendar.selectDate': 'Choose date',
    'calendar.previousMonth': 'Previous month',
    'calendar.nextMonth': 'Next month',
    'calendar.monthTitle': '{month}/{year}',
    'calendar.today': 'Today',

    'users.eyebrow': 'ACCESS CONTROL',
    'users.title': 'User Management',
    'users.subtitle': 'Create, edit, enable, disable and reset passwords',
    'users.username': 'Username',
    'users.displayName': 'Display name',
    'users.initialPassword': 'Initial password',
    'users.create': 'Create User',
    'users.loading': 'Loading users…',
    'users.user': 'User',
    'users.role': 'Role',
    'users.status': 'Status',
    'users.lastLogin': 'Last sign-in',
    'users.createdAt': 'Created',
    'users.actions': 'Actions',
    'users.resetPassword': 'Reset password',
    'users.enableUser': 'Enable this user',
    'users.currentRestriction': 'You cannot change your own role or enabled status here.',
    'users.passwordNotEmpty': 'The password cannot be empty. The password is not displayed or logged.',

    'streams.eyebrow': 'VIDEO CATALOG',
    'streams.title': 'Video Source Management',
    'streams.subtitle': 'Manage NVRs, GB28181 channels and OAPM settings in one place.',
    'streams.customAreas': '+ Custom Areas',
    'streams.refreshStatus': 'Refresh Status',
    'streams.syncNow': 'Synchronize Now',
    'streams.sources': 'Video Sources',
    'streams.online': 'Online',
    'streams.disabled': 'Disabled',
    'streams.discovered': 'Newly Discovered',
    'streams.nvrs': 'NVR Devices',
    'streams.syncLogs': 'Sync Logs',
    'streams.editNvr': 'Edit NVR Name',
    'streams.nvrDisplayName': 'NVR display name',
    'streams.nvrNameSaved': 'NVR name saved.',
    'streams.channels': 'Discovered channels',
    'streams.imported': 'Imported',
    'streams.missing': 'Missing',
    'streams.lastSync': 'Last sync',
    'streams.editSource': 'Edit Video Source',
    'streams.area': 'Area',
    'streams.manageAreas': 'Create / Manage',
    'streams.sortOrder': 'Sort order',
    'streams.enableOapm': 'Enable in OAPM',
    'streams.notes': 'Notes',
    'streams.currentFilter': 'Current filter',
    'streams.pageLimit': 'Up to 50 on this page',
    'streams.pageStats': 'Current page',
    'streams.waitImport': 'Awaiting import',
    'streams.onlineNvrs': '{count} online',
    'streams.searchPlaceholder': 'Search name, DeviceId, ChannelId or MainId',
    'streams.allAreas': 'All areas',
    'streams.allOnline': 'All online states',
    'streams.allEnabled': 'All enable states',
    'streams.allDiscovery': 'All discovery states',
    'streams.ignored': 'Ignored',
    'streams.source': 'Video source',
    'streams.areaNvr': 'Area / NVR',
    'streams.origin': 'Origin',
    'streams.catalogStatus': 'Catalog state',
    'streams.actions': 'Actions',
    'streams.reading': 'Loading catalog…',
    'streams.noResults': 'No sources match the current filters.',
    'streams.unassigned': 'Unassigned',
    'streams.manualSource': 'Manual source',
    'streams.activated': 'Activated',
    'streams.notActivated': 'Not activated',
    'streams.oapmEnabled': 'Enabled in OAPM',
    'streams.oapmDisabled': 'Disabled in OAPM',
    'streams.import': 'Import',
    'streams.ignore': 'Ignore',
    'streams.pageSummary': '{count} sources, page {page} / {pages}',
    'streams.previousPage': 'Previous',
    'streams.nextPage': 'Next',
    'streams.time': 'Time',
    'streams.trigger': 'Trigger',
    'streams.status': 'Status',
    'streams.added': 'Added',
    'streams.updated': 'Updated',
    'streams.error': 'Error',
    'streams.noArea': 'No area',
    'streams.areaDirectory': 'Custom areas',
    'streams.areaHelp': 'Create warehouse, office and entrance groups, then assign sources.',
    'streams.newArea': 'New area',
    'streams.name': 'Name',
    'streams.enableArea': 'Enable area',
    'streams.saveArea': 'Save area',
    'streams.areaCount': '{count} sources · order {order} · {status}',
    'streams.confirmDeleteArea': 'Delete area “{name}”? It must contain no sources.',
    'streams.confirmIgnore': 'Ignore “{name}”? It will be hidden from normal playback catalogs.',
    'streams.syncSummary': 'Sync complete: {added} added, {updated} updated, {missing} missing.',
    'streams.syncSuccess': 'Synchronization completed.',
    'streams.sourceSaved': 'Video source settings saved.',
    'streams.sourceImported': 'Video source imported.',
    'streams.sourceIgnored': 'Video source ignored.',
    'streams.areaUpdated': 'Area “{name}” updated.',
    'streams.areaCreated': 'Area “{name}” created.',
    'streams.areaCreatedSelected': 'Area “{name}” created and selected.',
    'streams.areaDeleted': 'Area deleted.',

    'system.eyebrow': 'OPERATIONS',
    'system.title': 'System Status',
    'system.refresh': 'Refresh Now',
    'system.services': 'Service Health',
    'system.checking': 'Checking',
    'system.checkedAt': 'Checked at {time}',
    'system.resources': 'Server Resources',
    'system.resourceHint': 'Current sample; no long-term history is stored',
    'system.cpu': 'CPU',
    'system.memory': 'Memory',
    'system.disk': 'Disk {name}',
    'system.uptime': 'System Uptime',
    'system.apiProcess': 'OAPM API Process',
    'system.mediaProcess': 'MediaServer Process',
    'system.network': 'Network',
    'system.upload': 'Current upload',
    'system.download': 'Current download',
    'system.totalSent': 'Total sent',
    'system.totalReceived': 'Total received',
    'system.videoBusiness': 'Video Operations',
    'system.currentSessions': 'Current OAPM in-memory sessions',
    'system.upstreams': 'Upstreams',
    'system.sessions': 'Sessions',
    'system.users': 'Users',
    'system.recentErrors': 'Recent Error Summary',
    'system.loadFailed': 'Failed to load system status.',
  },
  ja: {
    'app.name': 'OA Plaza 監視',
    'app.fullName': 'OAPlazaMonitor',
    'app.company': '株式会社オーエープラザ',
    'app.returnOverview': '概要へ戻る',
    'nav.overview': '概要',
    'nav.live': 'リアルタイム監視',
    'nav.playback': '録画再生',
    'nav.streams': '映像ソース管理',
    'nav.system': 'システム状態',
    'nav.main': 'メインナビゲーション',
    'language.label': '言語',
    'language.zh': '中文',
    'language.en': 'English',
    'language.ja': '日本語',

    'splash.bootAria': 'OAPlazaMonitor を起動しています',
    'splash.loginAria': '監視ワークスペースを開いています',
    'splash.subtitle': 'リアルタイム監視・NVR録画再生ワークスペース',
    'splash.booting': '監視ワークスペースを読み込んでいます',
    'splash.loginSuccess': 'ログインしました。ワークスペースを開いています',
    'splash.ready': '準備完了',

    'login.title': '社内監視コンソール',
    'login.username': 'ユーザー名',
    'login.password': 'パスワード',
    'login.submit': 'ログイン',
    'login.submitting': 'ログイン中',
    'login.failed': 'ログインに失敗しました。',

    'account.admin': '管理者',
    'account.user': '一般ユーザー',
    'account.editCurrent': '現在のユーザーを編集',
    'account.changePassword': 'パスワード変更',
    'account.manageUsers': 'ユーザー管理',
    'account.logout': 'ログアウト',
    'account.currentUser': '現在のユーザー',
    'account.username': 'ユーザー名',
    'account.role': '権限',
    'account.displayName': '表示名',
    'account.currentPassword': '現在のパスワード',
    'account.newPassword': '新しいパスワード',
    'account.confirmPassword': '新しいパスワードの確認',
    'account.passwordMismatch': '新しいパスワードが一致しません。',
    'account.profileUpdated': '表示名を更新しました。',
    'account.passwordUpdated': 'パスワードを変更しました。',
    'account.passwordHint': 'パスワードは空にできません。現在のログインセッションは維持されます。',
    'common.close': '閉じる',
    'common.cancel': 'キャンセル',
    'common.save': '保存',
    'common.edit': '編集',
    'common.delete': '削除',
    'common.refresh': '更新',
    'common.refreshing': '更新中',
    'common.processing': '処理中',
    'common.loading': '読み込み中…',
    'common.enabled': '有効',
    'common.disabled': '無効',
    'common.online': 'オンライン',
    'common.offline': 'オフライン',
    'common.unknown': '不明',
    'common.previous': '前へ',
    'common.next': '次へ',
    'common.fullscreen': '全画面',
    'common.exitFullscreen': '全画面を終了',
    'common.play': '再生',
    'common.pause': '一時停止',
    'common.stop': '停止',
    'common.back10': '10秒戻る',
    'common.forward10': '10秒進む',
    'common.retry': '再接続',
    'common.neverLogin': '未ログイン',
    'common.operationFailed': '操作に失敗しました。',

    'overview.eyebrow': 'BUSINESS OVERVIEW',
    'overview.title': '概要',
    'overview.reading': '読み込み中',
    'overview.updatedAt': '{time} 更新',
    'overview.brandTitle': '監視・録画再生ワークスペース',
    'overview.live': 'リアルタイム監視',
    'overview.playback': '録画再生',
    'overview.streams': '映像ソース管理',
    'overview.videoSources': '映像ソース',
    'overview.onlineCount': 'オンライン {count} チャンネル',
    'overview.offlineSources': 'オフライン',
    'overview.offlineDetail': '有効ですが現在オフライン',
    'overview.disabledSources': '無効',
    'overview.disabledDetail': '通常の再生一覧には表示されません',
    'overview.activeUpstreams': 'アクティブ上流',
    'overview.upstreamDetail': 'AKStream / ZLMediaKit',
    'overview.liveSessions': 'ライブ視聴セッション',
    'overview.activeUsers': 'アクティブユーザー {count} 名',
    'overview.playbackSessions': '録画再生セッション',
    'overview.playbackDetail': '現在の履歴ストリーム',
    'overview.recentEvents': 'RECENT EVENTS',
    'overview.recentErrors': '最近のエラー概要',
    'overview.media': 'メディア',
    'overview.system': 'システム',
    'overview.loadFailed': '業務概要の読み込みに失敗しました。',

    'live.batchTitle': '一括再生',
    'live.importedCount': '{count} チャンネル登録済み',
    'live.singleTitle': '個別再生',
    'live.singleCount': '選択可能なカメラ {count} 台',
    'live.search': '映像ソースを検索',
    'live.allStatus': 'すべての状態',
    'live.noSources': '条件に一致する映像ソースがありません。',
    'live.loadingCatalog': 'カタログを読み込んでいます…',
    'live.catalogFailed': '映像ソースカタログの読み込みに失敗しました。',
    'live.dragTitle': 'クリックで最初の空き枠へ追加、または任意の枠へドラッグ',
    'live.emptyTitle': '映像はまだ読み込まれていません',
    'live.emptyHint': '左側からカメラを選択またはドラッグして再生してください',
    'live.loadingStream': 'NVR ライブ映像を要求中',
    'live.reconnect': '再接続',
    'live.close': '閉じる',
    'live.layoutLabel': '{columns}×{columns} 監視レイアウト',
    'live.workspaceNotReady': 'リアルタイム監視ワークスペースはまだ準備できていません。',
    'live.workspaceTimeout': 'ワークスペース取得がタイムアウトしました。更新して再試行してください。',
    'live.workspaceFailed': 'ワークスペースを取得できません。更新して再試行してください。',
    'live.workspaceClaimed': 'このページが監視ワークスペースを引き継ぎ、古いセッションを整理しました。',
    'live.workspaceTaken': '別のページが監視ワークスペースを引き継ぎました。更新して再接続してください。',
    'live.workspaceTakenTile': '別のページがワークスペースを引き継ぎました。ページを更新してください。',
    'live.sessionExpired': '視聴セッションの期限が切れました。再接続してください。',
    'live.unknownSessionsCleaned': 'ブラウザが認識していない視聴セッションを {count} 件整理しました。',
    'live.loginExpired': 'ログインセッションの期限が切れました。再ログインしてください。',
    'live.alreadyInTile': '「{name}」は画面 {index} ですでに再生されています。',
    'live.alreadyAssigned': '「{name}」は画面 {index} に割り当て済みです。復旧する場合は「再接続」を使用してください。',
    'live.noEmptyTile': '現在のレイアウトに空き枠がありません。既存の画面へドラッグして置き換えてください。',
    'live.nvrAlreadyRunning': '「{name}」の {count} チャンネルはバックグラウンドで継続中です。',
    'live.nvrStarting': '「{name}」の {count} チャンネルを順次表示しています。',
    'live.nvrSubmitted': '「{name}」の {count} チャンネルを送信しました。映像を待っています。',
    'live.startTimeout': 'NVR ライブ映像の開始がタイムアウトしました。ワークスペースが回収します。',
    'live.startFailed': 'ライブ映像の開始に失敗しました。',
    'live.mediaTimeout': '{seconds} 秒以内に映像が表示されなかったため、視聴セッションを解放しました。',
    'live.mediaPlayerError': '再試行後もブラウザで再生できなかったため、視聴セッションを解放しました。',
    'live.releaseTimeout': '視聴セッションの解放がタイムアウトしました。ローカル枠は消去済みです。',
    'live.sourceGroupHint': '{count} チャンネルを 4×4 監視マトリクスへ表示',
    'live.previousCamera': '前のカメラ',
    'live.nextCamera': '次のカメラ',
    'live.mediaFailureReportTimeout': 'メディア障害の通知がタイムアウトしました。ローカルセッションは解放済みです。',
    'live.workspaceHeartbeatTimeout': 'ライブワークスペースのハートビートがタイムアウトしました。',
    'live.positionSyncTimeout': '画面位置の同期がタイムアウトしました。',
    'live.positionSyncFailed': '画面は入れ替わりましたが、セッション位置を同期できませんでした。',
    'live.positionSyncFailedDetail': '画面は入れ替わりましたが、セッション位置を同期できませんでした：{detail}',
    'live.tileName': '画面 {index}',

    'playback.eyebrow': 'NVR PLAYBACK',
    'playback.title': '録画再生',
    'playback.selectSource': '映像ソースを選択してください',
    'playback.waitQuery': '検索待ち',
    'playback.noSources': '録画検索に対応する映像ソースがありません',
    'playback.searchSource': 'カメラ名・エリア・NVR を検索',
    'playback.chooseSource': '録画対応カメラを選択',
    'playback.query': '録画を検索',
    'playback.querying': '検索中…',
    'playback.statusQuerying': '録画を検索中',
    'playback.statusOpening': '履歴ストリームを開始中',
    'playback.statusSeeking': '指定時刻からストリームを再構築中',
    'playback.stop': '再生停止',
    'playback.unclassified': '未分類',
    'playback.unknownNvr': '不明な NVR',
    'playback.nvrClock': 'NVR 時刻：{date} · タイムゾーン：{zone} · カタログ：{received}/{total}',
    'playback.segments': '録画区間',
    'playback.noSegments': '録画区間はまだ取得されていません。',
    'playback.audioUnsupported': '音声非対応',
    'playback.enableAudio': '音声を有効化',
    'playback.mute': 'ミュート',
    'playback.volume': '音量',
    'playback.rate': '速度',
    'playback.timelineLabel': '24時間録画タイムライン',
    'playback.dateLabel': '録画検索日を選択',
    'playback.chooseSourceError': '先に映像ソースを選択してください。',
    'playback.queryFailed': '録画検索に失敗しました。',
    'playback.playFailed': '履歴ストリームの開始に失敗しました。',
    'playback.noRecordingAt': '{time} に録画はありません。',
    'playback.noRecordingStatus': 'この時刻に録画はありません',
    'playback.firstSegment': '前の録画区間',
    'playback.nextSegment': '次の録画区間',
    'playback.pausedAt': '録画を一時停止 · {time}',
    'playback.playingAt': '録画再生中 · {time}',
    'playback.stopped': '録画再生を停止しました',
    'playback.queryDateChanged': '日付を変更しました。再検索してください。',
    'playback.foundSegments': '録画区間を {count} 件検出{suffix}',
    'playback.incompleteSuffix': '（カタログ未完了）',
    'playback.noRecordingDay': 'この日の録画はありません',
    'playback.queryTimeout': '録画カタログが返されませんでした。後でもう一度お試しください。',
    'playback.queryPartial': '{seconds} 秒以内にカタログが完了しなかったため、受信済み {count} 件を表示します。',
    'playback.reinviteNotice': '新しい GB28181 INVITE で指定時刻から再開しました。INFO シークは使用しません。',
    'playback.reinviteDirect': '新しい GB28181 INVITE で指定時刻から直接開始しました。',
    'playback.audioUnavailableNotice': 'この録画の音声コーデックは非対応です。映像のみの再生へ復旧しました。',
    'playback.audioUnavailableStatus': '音声は利用不可、映像は継続中',
    'playback.selectFirst': '先に録画を再生してください。',
    'playback.sourcesLoadFailed': '録画対応カメラの読み込みに失敗しました。',
    'playback.heartbeatTimeout': '録画再生のハートビートがタイムアウトしました。',
    'playback.rebuildAt': '{time} から履歴ストリームを再構築中',
    'playback.rebuildTimeout': '指定時刻のストリーム再構築がタイムアウトしました。現在のセッションは維持されます。',
    'playback.targetReady': '指定時刻のストリームを作成しました。映像を待っています。',
    'playback.rebuildFailed': '指定時刻のストリーム再構築に失敗しました。',
    'playback.originalContinues': '再構築に失敗しました。現在の録画は継続できます。',
    'playback.stopUnconfirmedLocal': '録画停止を確認できませんでした。ローカルセッションは解放済みです。',
    'playback.stopBackgroundCleanup': '録画停止を確認できませんでした。バックグラウンドで回収を続けます。',
    'playback.creatingQuery': '録画カタログ照会を作成中',
    'playback.queryTask': 'taskId {taskId} を照会中',
    'playback.readResultTimeout': '録画カタログ結果の読み込みがタイムアウトしました。',
    'playback.receivingCatalog': '録画カタログを受信中 {received}/{total}',
    'playback.queryFailedStatus': '照会失敗',
    'playback.openingAt': '{time} から履歴ストリームを開始中',
    'playback.openTimeout': '履歴ストリームの開始がタイムアウトしました。後でもう一度お試しください。',
    'playback.releaseExpiredTimeout': '古い録画セッションの解放がタイムアウトしました。',
    'playback.startedWaiting': '履歴ストリームを開始しました。映像を待っています。',
    'playback.playFailedStatus': '再生失敗',
    'playback.audioUnsupportedRetry': 'この音声コーデックは非対応です。映像はミュートのまま継続します。区間変更後に再試行できます。',
    'playback.tryingAudio': '録画音声トラックへ接続中',

    'player.idle': '視聴するカメラを選択してください',
    'player.connecting': 'メディアストリームへ接続中',
    'player.unsupported': 'このブラウザは MSE/FLV 再生に対応していません。',
    'player.playFailed': 'プレーヤーを開始できませんでした。',
    'player.resumeFailed': '再生を再開できませんでした。',
    'player.paused': '一時停止',
    'player.diagnostics': '診断情報を表示',
    'player.codecUnsupported': 'このブラウザではメディアコーデックまたはコンテナを再生できません。診断情報を確認してください。',
    'player.mediaError': 'メディアエラー：{detail}',
    'player.backgroundPaused': 'ブラウザがバックグラウンド映像を一時停止しました。ページへ戻ると自動再開します。',
    'player.retrying': 'メディアエンドポイントを再試行中（{attempt} 回目）',
    'player.retryLater': 'メディアが未準備です。{delay} ms 後に再試行します',
    'player.audioFallback': '録画音声コーデックは非対応です。映像のみへ復旧しています',

    'calendar.selectDate': '日付を選択',
    'calendar.previousMonth': '前の月',
    'calendar.nextMonth': '次の月',
    'calendar.monthTitle': '{year}年{month}月',
    'calendar.today': '今日',

    'users.eyebrow': 'ACCESS CONTROL',
    'users.title': 'ユーザー管理',
    'users.subtitle': '作成・編集・有効化・無効化・パスワード再設定',
    'users.username': 'ユーザー名',
    'users.displayName': '表示名',
    'users.initialPassword': '初期パスワード',
    'users.create': 'ユーザー作成',
    'users.loading': 'ユーザーを読み込んでいます…',
    'users.user': 'ユーザー',
    'users.role': '権限',
    'users.status': '状態',
    'users.lastLogin': '最終ログイン',
    'users.createdAt': '作成日時',
    'users.actions': '操作',
    'users.resetPassword': 'パスワード再設定',
    'users.enableUser': 'このユーザーを有効化',
    'users.currentRestriction': 'ここでは自分自身の権限や有効状態を変更できません。',
    'users.passwordNotEmpty': 'パスワードは空にできません。パスワード内容は表示・記録されません。',

    'streams.eyebrow': 'VIDEO CATALOG',
    'streams.title': '映像ソース管理',
    'streams.subtitle': 'NVR・GB28181 チャンネル・OAPM 設定を一元管理します。',
    'streams.customAreas': '＋ カスタムエリア',
    'streams.refreshStatus': '状態更新',
    'streams.syncNow': '今すぐ同期',
    'streams.sources': '映像ソース',
    'streams.online': 'オンライン',
    'streams.disabled': '無効',
    'streams.discovered': '新規検出',
    'streams.nvrs': 'NVR 機器',
    'streams.syncLogs': '同期履歴',
    'streams.editNvr': 'NVR 名称を編集',
    'streams.nvrDisplayName': 'NVR 表示名',
    'streams.nvrNameSaved': 'NVR 名称を保存しました。',
    'streams.channels': '検出チャンネル',
    'streams.imported': '取込済み',
    'streams.missing': '切断',
    'streams.lastSync': '最終同期',
    'streams.editSource': '映像ソースを編集',
    'streams.area': '所属エリア',
    'streams.manageAreas': '新規 / 管理',
    'streams.sortOrder': '並び順',
    'streams.enableOapm': 'OAPM で有効化',
    'streams.notes': 'メモ',
    'streams.currentFilter': '現在の絞り込み',
    'streams.pageLimit': 'このページは最大 50 件',
    'streams.pageStats': 'このページの集計',
    'streams.waitImport': '取込待ち',
    'streams.onlineNvrs': 'オンライン {count} 台',
    'streams.searchPlaceholder': '名前、DeviceId、ChannelId、MainId を検索',
    'streams.allAreas': 'すべてのエリア',
    'streams.allOnline': 'すべてのオンライン状態',
    'streams.allEnabled': 'すべての有効状態',
    'streams.allDiscovery': 'すべての検出状態',
    'streams.ignored': '無視済み',
    'streams.source': '映像ソース',
    'streams.areaNvr': 'エリア / NVR',
    'streams.origin': '取得元',
    'streams.catalogStatus': 'カタログ状態',
    'streams.actions': '操作',
    'streams.reading': 'カタログを読み込んでいます…',
    'streams.noResults': '現在の条件に一致する映像ソースはありません。',
    'streams.unassigned': '未割当',
    'streams.manualSource': '手動ソース',
    'streams.activated': '有効化済み',
    'streams.notActivated': '未有効化',
    'streams.oapmEnabled': 'OAPM で有効',
    'streams.oapmDisabled': 'OAPM で無効',
    'streams.import': '取込',
    'streams.ignore': '無視',
    'streams.pageSummary': '全 {count} 件、{page} / {pages} ページ',
    'streams.previousPage': '前へ',
    'streams.nextPage': '次へ',
    'streams.time': '時刻',
    'streams.trigger': 'トリガー',
    'streams.status': '状態',
    'streams.added': '追加',
    'streams.updated': '更新',
    'streams.error': 'エラー',
    'streams.noArea': 'エリア指定なし',
    'streams.areaDirectory': 'カスタムエリア',
    'streams.areaHelp': '倉庫、事務所、出入口などのグループを作成し、映像ソースへ割り当てます。',
    'streams.newArea': '新規エリア',
    'streams.name': '名前',
    'streams.enableArea': 'エリアを有効化',
    'streams.saveArea': 'エリアを保存',
    'streams.areaCount': '{count} 件 · 並び順 {order} · {status}',
    'streams.confirmDeleteArea': 'エリア「{name}」を削除しますか？映像ソースが含まれていない必要があります。',
    'streams.confirmIgnore': '映像ソース「{name}」を無視しますか？通常の再生一覧から非表示になります。',
    'streams.syncSummary': '同期完了：追加 {added}、更新 {updated}、切断 {missing}。',
    'streams.syncSuccess': '同期が完了しました。',
    'streams.sourceSaved': '映像ソース設定を保存しました。',
    'streams.sourceImported': '映像ソースを取り込みました。',
    'streams.sourceIgnored': '映像ソースを無視しました。',
    'streams.areaUpdated': 'エリア「{name}」を更新しました。',
    'streams.areaCreated': 'エリア「{name}」を作成しました。',
    'streams.areaCreatedSelected': 'エリア「{name}」を作成して選択しました。',
    'streams.areaDeleted': 'エリアを削除しました。',

    'system.eyebrow': 'OPERATIONS',
    'system.title': 'システム状態',
    'system.refresh': '今すぐ更新',
    'system.services': 'サービス状態',
    'system.checking': '確認中',
    'system.checkedAt': '{time} 確認',
    'system.resources': 'サーバーリソース',
    'system.resourceHint': '現在値のみ。長期履歴は保存しません',
    'system.cpu': 'CPU',
    'system.memory': 'メモリ',
    'system.disk': 'ディスク {name}',
    'system.uptime': 'システム稼働時間',
    'system.apiProcess': 'OAPM API プロセス',
    'system.mediaProcess': 'MediaServer プロセス',
    'system.network': 'ネットワーク状態',
    'system.upload': '現在の送信',
    'system.download': '現在の受信',
    'system.totalSent': '累計送信',
    'system.totalReceived': '累計受信',
    'system.videoBusiness': '映像業務状態',
    'system.currentSessions': '現在の OAPM メモリセッション',
    'system.upstreams': '上流ストリーム',
    'system.sessions': 'セッション',
    'system.users': 'ユーザー',
    'system.recentErrors': '最近のエラー概要',
    'system.loadFailed': 'システム状態の読み込みに失敗しました。',
  },
};

function detectInitialLanguage(): Language {
  const stored = window.localStorage.getItem(storageKey);
  if (stored === 'zh' || stored === 'en' || stored === 'ja') return stored;

  const browserLanguage = navigator.language.toLocaleLowerCase();
  if (browserLanguage.startsWith('ja')) return 'ja';
  if (browserLanguage.startsWith('en')) return 'en';
  return 'zh';
}

function interpolate(template: string, values?: TranslationValues): string {
  if (!values) return template;
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language === 'ja' ? 'ja-JP' : 'en';
  }, [language]);

  const value = useMemo<I18nContextValue>(() => {
    const setLanguage = (next: Language) => {
      window.localStorage.setItem(storageKey, next);
      document.documentElement.lang = next === 'zh' ? 'zh-CN' : next === 'ja' ? 'ja-JP' : 'en';
      setLanguageState(next);
    };

    const t = (key: string, values?: TranslationValues) => {
      const template = messages[language][key] ?? messages.zh[key] ?? key;
      return interpolate(template, values);
    };

    return {
      language,
      locale: languageMeta[language].locale,
      setLanguage,
      t,
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider.');
  return context;
}

export function LanguageSelector({ className = '' }: { className?: string }) {
  const { language, setLanguage, t } = useI18n();

  return (
    <label className={`language-selector ${className}`.trim()} title={t('language.label')}>
      <span className="language-selector-icon" aria-hidden="true">文</span>
      <select
        value={language}
        aria-label={t('language.label')}
        onChange={(event) => setLanguage(event.target.value as Language)}
      >
        {(Object.keys(languageMeta) as Language[]).map((key) => (
          <option key={key} value={key}>{languageMeta[key].short}</option>
        ))}
      </select>
    </label>
  );
}
